import React from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box,
    createStyles,
    makeStyles,
    useMediaQuery
} from '@material-ui/core';

import {
    hasAlreadyAssurance,
    IResumeOffre,
    isRevisableFerme,
    isVariableProtege,
    isVariableReduit,
    ParcoursAssurance
} from '../../hooks';
import {
    ContainerMui,
    Variant,
    Typography,
    ColorHex,
    DateText,
    Police,
    Hr,
    Spacer
} from '../../styles';
import { TexteAvecInfoBulle } from '../texteAvecInfoBulle/TexteAvecInfoBulle';
import { getFormattedDate } from '../../utils';

export interface IResumeMontantProps {
    resumeOffre: IResumeOffre;
    dateDuJour: Date;
    isAjoutAssurance: boolean;
    isDetailAssurance?: boolean;
}

const useStyles = makeStyles(() =>
    createStyles({
        titrePaiement: {
            fontSize: '20px !important'
        },
        paiement: {
            fontSize: '20px !important'
        },
        sousTitre: {
            fontSize: '16px !important',
            fontWeight: 400
        }
    })
);

export const ResumeMontant: React.FC<IResumeMontantProps> = ({
    resumeOffre,
    dateDuJour,
    isAjoutAssurance,
    isDetailAssurance
}) => {
    const [t] = useTranslation(['ResumeMontant']);
    const classes = useStyles();
    const isMobile = useMediaQuery('(max-width:640px)');

    // Variables utilisé dans l'affichage du resume montant
    const { offreChoisie, assurance, isAdmissibleSignatureIntegree } =
        resumeOffre;
    const codeAssurance = assurance?.codeAssurance;
    const dateProchainPaiement = new Date(
        getFormattedDate(resumeOffre?.dateProchainPaiement, '/')
    );
    const montantRemboursement = offreChoisie?.montantRemboursement;
    const montantRemboursementSansAssurance =
        offreChoisie?.montantRemboursementSansAssurance;
    const frequenceRemboursement = offreChoisie?.frequenceRemboursement;
    const isOfferVariableProtege = isVariableProtege(offreChoisie);
    const isOfferVariableReduit = isVariableReduit(offreChoisie);
    const isOffreRevisableFerme = isRevisableFerme(offreChoisie);
    const has5Dans1Note = offreChoisie?.has5Dans1Note;
    const parcoursAssurance = isAjoutAssurance
        ? resumeOffre.assurance.codeAdmissibleAssurance
        : ParcoursAssurance.NON_ADMISSIBLE;

    const renderReconductionAssuranceOuAucunAssurance = () => {
        return (
            <>
                {isAdmissibleSignatureIntegree ? (
                    <Box
                        display="flex"
                        flexWrap="nowrap"
                        alignItems="flex-start"
                    >
                        <Typography
                            variant={Variant.h4}
                            police={Police.bold}
                            color={ColorHex.darkPrimary}
                            marginBottom="0.5rem"
                            className={classes.titrePaiement}
                        >
                            {t(
                                hasAlreadyAssurance(codeAssurance)
                                    ? 'titrePaiementAvecRedonductionAssurance'
                                    : 'titrePaiementSansAssurance'
                            )}
                        </Typography>
                    </Box>
                ) : (
                    <>
                        <TexteAvecInfoBulle
                            label={t(
                                hasAlreadyAssurance(codeAssurance)
                                    ? 'titrePaiementAvecRedonductionAssurance'
                                    : 'titrePaiementSansAssurance'
                            )}
                            labelPolice={Police.bold}
                            labelVariant={Variant.h4}
                            labelColor={ColorHex.darkPrimary}
                            labelInfoBulle={t('titreInfoBulleLibelle')}
                            labelHorsEcranInfoBulle={t(
                                'titreInfoBulleHorsEcran'
                            )}
                            isSmallerLabelInfobulle={isMobile}
                            labelClass={classes.titrePaiement}
                            // TODO: remettre le dataTestId avec update dsd
                            // dataTestId="info_bulle_paiement"
                        />
                        <Spacer all={3} />
                    </>
                )}
                {isAdmissibleSignatureIntegree ? (
                    <Typography
                        variant={Variant.p}
                        color={ColorHex.darkPrimary}
                        marginBottom="0.5rem"
                    >
                        <DateText
                            date={dateProchainPaiement}
                            translationKey="ResumeMontant:sousTitrePaiementSI"
                        >
                            {{ date: dateProchainPaiement }}
                        </DateText>
                    </Typography>
                ) : (
                    <Typography
                        variant={Variant.p}
                        color={ColorHex.darkPrimary}
                        marginBottom="0.5rem"
                    >
                        <DateText
                            date={dateDuJour}
                            translationKey="ResumeMontant:sousTitrePaiement"
                        >
                            {{ date: dateDuJour }}
                        </DateText>
                    </Typography>
                )}
                <Typography
                    variant={Variant.p}
                    color={ColorHex.darkPrimary}
                    hideMarginBottom
                >
                    <span className={classes.paiement}>
                        <strong>
                            {t('Number:currency', {
                                value: montantRemboursement
                            })}
                        </strong>
                    </span>
                    <strong>{t(frequenceRemboursement)}</strong>
                    {has5Dans1Note && (
                        <span>
                            {t('note5dans1_1')}
                            <sup>{t('note5dans1_2')}</sup>
                            {t('note5dans1_3')}
                        </span>
                    )}
                    {isOfferVariableProtege && t('noteVariableProtege')}
                </Typography>
            </>
        );
    };

    const renderAjoutAssurance = () => {
        return (
            <>
                <Typography
                    variant={Variant.h4}
                    police={Police.bold}
                    color={ColorHex.darkPrimary}
                    className={classes.titrePaiement}
                    marginBottom="0.65rem"
                >
                    {(parcoursAssurance === ParcoursAssurance.PARCOURS_1 ||
                        parcoursAssurance === ParcoursAssurance.PARCOURS_2) &&
                        t('titrePaiementAssuranceP1P2')}
                    {parcoursAssurance === ParcoursAssurance.PARCOURS_3 &&
                        t('titrePaiementAssuranceP3')}
                </Typography>
                <Typography
                    variant={Variant.h5}
                    color={ColorHex.darkPrimary}
                    className={classes.sousTitre}
                    marginBottom="0.5rem"
                >
                    {t('sousTitrePaiementAjoutAssurance')}
                </Typography>
                <Typography
                    variant={Variant.p}
                    color={ColorHex.darkPrimary}
                    marginBottom="0.5rem"
                >
                    <span className={classes.paiement}>
                        <strong>
                            {t('Number:currency', {
                                value: montantRemboursement
                            })}
                        </strong>
                    </span>
                    <strong>{t(frequenceRemboursement)}</strong>
                    {has5Dans1Note && (
                        <span>
                            {t('note5dans1_1')}
                            <sup>{t('note5dans1_2')}</sup>
                            {t('note5dans1_3')}
                        </span>
                    )}
                </Typography>
                {isDetailAssurance && (
                    <>
                        <TexteAvecInfoBulle
                            label={
                                isOfferVariableProtege
                                    ? t('notePaiementAssuranceProtege_1') +
                                      t('Number:currency', {
                                          value: montantRemboursementSansAssurance
                                      }) +
                                      t('notePaiementAssuranceProtege_2') +
                                      t('Number:currency', {
                                          value:
                                              montantRemboursement -
                                              montantRemboursementSansAssurance
                                      }) +
                                      t('notePaiementAssuranceProtege_3')
                                    : t('notePaiementAssurance_1') +
                                      t('Number:currency', {
                                          value: montantRemboursementSansAssurance
                                      }) +
                                      t('notePaiementAssurance_2') +
                                      t('Number:currency', {
                                          value:
                                              montantRemboursement -
                                              montantRemboursementSansAssurance
                                      }) +
                                      t('notePaiementAssurance_3')
                            }
                            labelVariant={Variant.p}
                            labelColor={ColorHex.darkPrimary}
                            labelInfoBulle={
                                parcoursAssurance ===
                                    ParcoursAssurance.PARCOURS_1 ||
                                parcoursAssurance ===
                                    ParcoursAssurance.PARCOURS_2
                                    ? t(
                                          'notePaiementAssuranceInfoBulleLibelleP1P2'
                                      )
                                    : t(
                                          'notePaiementAssuranceInfoBulleLibelleP3'
                                      )
                            }
                            labelHorsEcranInfoBulle={t(
                                'notePaiementAssuranceInfoBulleHorsEcran'
                            )}
                            isSmallerLabelInfobulle={isMobile}
                        />
                        <Spacer all={4} />
                    </>
                )}
                <Hr backgroundColor={ColorHex.primary2} />

                {isDetailAssurance ? (
                    <>
                        <TexteAvecInfoBulle
                            label={
                                isOfferVariableProtege ||
                                isOffreRevisableFerme ||
                                isOfferVariableReduit
                                    ? t('sousTitreTauxAjoutAssuranceOffreAutre')
                                    : t('sousTitreTauxAjoutAssuranceOffreFixe')
                            }
                            labelVariant={Variant.h5}
                            labelColor={ColorHex.darkPrimary}
                            labelClass={classes.sousTitre}
                            labelInfoBulle={
                                isOfferVariableProtege ||
                                isOffreRevisableFerme ||
                                isOfferVariableReduit
                                    ? t(
                                          'labelInfoBullesousTitreTauxAjoutAssuranceOffreAutre_1'
                                      ) +
                                      t('Date:years', {
                                          value: offreChoisie?.valeurDureeTerme
                                      }) +
                                      t(
                                          'labelInfoBullesousTitreTauxAjoutAssuranceOffreAutre_2'
                                      )
                                    : t(
                                          'labelInfoBullesousTitreTauxAjoutAssuranceOffreFixe'
                                      )
                            }
                            labelHorsEcranInfoBulle={t(
                                'labelHorsEcransousTitreTauxAjoutAssuranceOffre'
                            )}
                            isSmallerLabelInfobulle={isMobile}
                        />
                        <Spacer all={3} />
                    </>
                ) : (
                    <Typography
                        variant={Variant.h5}
                        color={ColorHex.darkPrimary}
                        className={classes.sousTitre}
                        marginBottom="0.5rem"
                    >
                        {isOfferVariableProtege ||
                        isOffreRevisableFerme ||
                        isOfferVariableReduit
                            ? t('sousTitreTauxAjoutAssuranceOffreAutre')
                            : t('sousTitreTauxAjoutAssuranceOffreFixe')}
                    </Typography>
                )}
                <Typography
                    variant={Variant.p}
                    color={ColorHex.darkPrimary}
                    police={Police.bold}
                    className={classes.paiement}
                    marginBottom={isDetailAssurance ? '0.5rem' : '0'}
                >
                    {t('Number:percent3Decimal', {
                        value: offreChoisie?.tauxIncluantAssurance
                    })}
                </Typography>

                {isDetailAssurance && (
                    <Typography
                        variant={Variant.p}
                        color={ColorHex.darkPrimary}
                        hideMarginBottom
                    >
                        {getNoteTauxAjoutAssurance()}
                    </Typography>
                )}
            </>
        );
    };

    const getNoteTauxAjoutAssurance = () => {
        const tauxAdditionnelAssurance = t('Number:percent3Decimal', {
            value: offreChoisie?.tauxAdditionnelAssurance
        });

        return (
            t('noteTauxAjoutAssuranceOffre_1') +
            tauxAdditionnelAssurance +
            t('noteTauxAjoutAssuranceOffre_2')
        );
    };

    return (
        <ContainerMui
            backgroundColor={ColorHex.lightConfirmation3}
            borderRadius="0.5rem"
            dataTestId={'boite_verte_resume_paiement'}
            padding={isMobile ? '1rem' : '1.5rem'}
            noSpacer
        >
            {isAjoutAssurance
                ? renderAjoutAssurance()
                : renderReconductionAssuranceOuAucunAssurance()}
        </ContainerMui>
    );
};
